/**
 * context — the RENDER CONTEXT (README §4.3) both runtimes build identically:
 *
 *   { entries[], settings, instance_id, page_args, appearance, locale, user,
 *     pending{adds[],updates[]}, selectors{…evaluated}, title, accent }
 *
 * Selectors are the DATA facet: pure, recomputed on EVERY feed change, never
 * mutated by the UI. `op ∈ {fold, filter, group, distinct, derive, latest}`;
 * `derive.expr` names a runtime-implemented derive (README §7.3 — a name this
 * build lacks degrades to EMPTY and is reported, never a crash). Selectors may
 * reference each other by name (`{op:'filter', from:'items'}`) and are
 * evaluated lazily-by-dependency, so declaration order doesn't matter.
 *
 * The context is FLAT for binds: every selector name, plus `entries`,
 * `settings`, `page_args`, `pending`, `user`, `title`, `accent`, `focusEntry`,
 * is a top-level key — that's what makes "medCounts.summary" and
 * "focusEntry.images" resolve with the same dotted reader.
 */
import { getPath, matchesWhere, resolveWhere } from './paths';
import { foldRows, normalizeEntry, validateFold } from './fold';
import { runDerive } from './derives';

/** Which bucket a row's `by` value belongs to; a `null` in a bucket's
 * `categories` makes it the catch-all for unknown/missing values. */
function bucketFor(value, buckets) {
  const v = value == null || value === '' ? null : String(value).toLowerCase();
  let fallback = null;
  for (const b of buckets) {
    const cats = (b.categories || []).map((c) => (c == null ? null : String(c).toLowerCase()));
    if (cats.includes(null)) fallback = fallback || b;
    if (v != null && cats.includes(v)) return b;
    if (v == null && cats.includes(null)) return b;
  }
  return fallback;
}

/**
 * A derive ARG is one of three things, and telling them apart matters:
 *   "goal"            a selector name        → its evaluated value
 *   "page_args.date"  a context PATH         → its value, or NULL when absent
 *   "cal"             a literal              → itself
 *
 * The middle case is the one that bites: `page_args.date` is legitimately
 * absent on first paint, and passing the literal string "page_args.date" into
 * `weekBars` produced seven bars dated `Invalid Date`. A dotted path rooted at
 * a real context key therefore resolves to null when missing, and only a
 * string that is NOT such a path stays a literal.
 */
function resolveArg(v, resolve, root) {
  if (typeof v !== 'string') return v;
  const viaSelector = resolve(v);
  if (viaSelector !== undefined) return viaSelector;
  const head = v.split('.')[0].split('[')[0];
  if (v.includes('.') && Object.prototype.hasOwnProperty.call(root, head)) {
    const viaPath = getPath(root, v);
    return viaPath === undefined ? null : viaPath;
  }
  return v;
}

/**
 * `{op:'latest', defaults:{…}}` — THE ROW A USER HAS NOT WRITTEN YET.
 *
 * `latest` answers with the newest row of a kind, and with `null` when the user
 * has never written one. That null is correct as data and wrong as a page: a
 * goal ring bound to `goal.calorie_target` on day one draws 320/0, and no
 * blueprint can fix it, because the bind grammar has no `??` and the defaults
 * do not belong in a component's props (three nodes read the same goal and
 * would each carry their own copy of the number).
 *
 * So the DATA lane states them once, beside the selector that may come back
 * empty. Merged UNDER the found row — a value the user HAS written always wins;
 * with no row at all the defaults ARE the row, so a first-run page reads the
 * same paths a returning user's does instead of branching on emptiness.
 *
 * A `null` on the row counts as "not written": a field-merge fold that carries
 * an explicit null for a target the user never set would otherwise shadow the
 * default with nothing, which is the exact blank the defaults exist to prevent.
 *
 * Absent `defaults` ⇒ byte-identical to before (the row, or null).
 */
function withDefaults(row, defaults) {
  if (!defaults || typeof defaults !== 'object' || Array.isArray(defaults)) return row;
  if (!row) return { ...defaults };
  const out = { ...defaults, ...row };
  for (const [k, v] of Object.entries(defaults)) {
    if (row[k] == null) out[k] = v;
  }
  return out;
}

/**
 * 统一结果页只展示自己 Session 的在途记录，并让服务端终态覆盖宿主首帧占位。
 *
 * `item_id` 入口没有可匹配的 Pending Session，因此显式条目未命中时必须返回 Missing；
 * 两种结果定位都不存在时逐字保留现有 pending，普通首页与旧蓝图行为不变。
 */
function resultPagePendingAdds(hostAdds, serverSessions, sourceSessionId, itemId) {
  const target = sourceSessionId == null ? '' : String(sourceSessionId).trim();
  if (!target) return itemId == null || String(itemId).trim() === '' ? hostAdds : [];
  const host = hostAdds.filter((row) => String(row?.session_id || '').trim() === target);
  const server = serverSessions.filter((row) => String(row?.session_id || '').trim() === target);
  if (!server.length) return host;
  const authoritative = server[0];
  return host.length ? [{ ...host[0], ...authoritative }] : [authoritative];
}

/**
 * Evaluate one selector spec. `resolve(name)` pulls (and memoises) a
 * dependency selector; `root` is the partially built context used for `where`
 * clauses and derive args that read `settings.*` / `page_args.*`; `feed` is the
 * whole folded item set, which a derive may read BESIDES its own `of` rows
 * (`distinctPhotos` marks the session that holds the pinned model photo — a
 * row that is deliberately not in its `of: pieces` input, and that it must not
 * reach through a second selector, since that selector reads it back).
 */
function evalSelector(spec, { entries, foldSpec, resolve, root, feed, onIssue }) {
  if (!spec || typeof spec !== 'object') return null;
  switch (spec.op) {
    case 'fold': {
      const kinds = spec.kind ? [spec.kind] : (spec.kinds || null);
      const rows = kinds ? entries.filter((e) => kinds.includes(e.kind)) : entries;
      return foldRows(rows, { ...foldSpec, ...(spec.fold || {}) });
    }
    case 'latest': {
      const kinds = spec.kind ? [spec.kind] : (spec.kinds || null);
      const rows = kinds ? entries.filter((e) => kinds.includes(e.kind)) : entries;
      const folded = foldRows(rows, { ...foldSpec, ...(spec.fold || {}) });
      return withDefaults(folded[0] || null, spec.defaults);
    }
    case 'filter': {
      const from = resolve(spec.from);
      const arr = Array.isArray(from) ? from : [];
      const where = resolveWhere(spec.where, root);
      return arr.filter((r) => matchesWhere(r, where));
    }
    case 'group': {
      const from = resolve(spec.from);
      const arr = Array.isArray(from) ? from : [];
      const by = spec.by || 'category';
      const buckets = Array.isArray(spec.buckets) ? spec.buckets : [];
      if (!buckets.length) {
        // No declared buckets ⇒ group by the distinct values present.
        const seen = new Map();
        for (const r of arr) {
          const k = String(getPath(r, by) ?? 'other');
          if (!seen.has(k)) seen.set(k, { id: k, label: k, items: [] });
          seen.get(k).items.push(r);
        }
        return [...seen.values()];
      }
      const out = buckets.map((b) => ({ id: b.id, label: b.label || b.id, items: [] }));
      const byId = new Map(out.map((b) => [b.id, b]));
      for (const r of arr) {
        const b = bucketFor(getPath(r, by), buckets);
        if (!b) continue;
        byId.get(b.id)?.items.push(r);
      }
      return out;
    }
    case 'distinct': {
      const from = resolve(spec.from);
      const arr = Array.isArray(from) ? from : [];
      const key = spec.by || spec.key || 'id';
      const seen = new Map();
      for (const r of arr) {
        const k = String(getPath(r, key) ?? '');
        if (!k || seen.has(k)) continue;
        seen.set(k, r);
      }
      return [...seen.values()];
    }
    case 'derive': {
      const rows = spec.of ? resolve(spec.of) : entries;
      const args = {};
      for (const [k, v] of Object.entries(spec.args || {})) {
        args[k] = resolveArg(v, resolve, root);
      }
      // A derive this build lacks (or one that throws) returns EMPTY and is
      // reported — it never becomes an `__unsupported` VALUE, because a value
      // is what a node binds, and a bound placeholder would replace a working
      // page with a red box (§7.6 as refined by §4.5).
      return runDerive(spec.expr, rows, args, feed, onIssue);
    }
    default:
      return { __unsupported: 'selector', name: spec.op };
  }
}

/**
 * Build the full render context for a page.
 *
 * `feed` is the host payload: `{ entries, settings, instance_id, pending }` —
 * the exact shape `GET /widget-instances/{id}/data` + the WebView host already
 * inject, so a NativeIR page and an HTML page see the same data.
 */
export function buildRenderContext({
  blueprint,
  feed = {},
  pageArgs = {},
  appearance = 'light',
  locale = 'en',
  // The app build to render AS (§4.5 graceful degradation). null = latest =
  // every component available, which is what the app and the studio's live
  // preview get; an older build makes the version gate degrade the components
  // that app never shipped, so an author can SEE what an old app shows.
  targetBuild = null,
  // THE FEED HAS NOT ARRIVED YET.
  //
  // Without this fact stated, "no rows" and "no answer yet" are the same
  // context: derives run over `[]`, so a gauge reads 0, a stat tile reads 0 and
  // an EmptyState says "add your first item" — to a user who holds forty. Then
  // the feed lands and every one of them flips. The page was never wrong about
  // the data; it was answering a question that had not been asked yet.
  //
  // Stating it here rather than in each component is what keeps it ONE rule:
  // the interpreter draws a skeleton for every data-bound node (see
  // `interpreter.jsx`), and nothing else in the kit has to learn about waiting.
  loading = false,
} = {}) {
  const bp = blueprint || {};
  const data = bp.data || {};
  const rawEntries = Array.isArray(feed.entries) ? feed.entries : [];
  const entries = rawEntries.map(normalizeEntry);
  const settings = { ...(data.settings || {}), ...(feed.settings || {}) };
  const pending = {
    adds: [], updates: [], deleting: [], deleting_grids: [],
    ...(feed.pending || {}),
  };
  pending.adds = resultPagePendingAdds(
    Array.isArray(pending.adds) ? pending.adds : [],
    Array.isArray(feed.pending_sessions) ? feed.pending_sessions : [],
    pageArgs?.source_session_id,
    pageArgs?.item_id,
  );

  /**
   * `user` — THE HOST'S OWN FACTS ABOUT THIS SURFACE, verbatim.
   *
   * The WebView host has always injected `__USER_DATA__`
   * (`{vi_user_id, name, appearance, locale, host_features[],
   * host_session_badge?}`); the native context had no word for it, so a
   * blueprint could not ask the one question three prod bundles ask first:
   * **is the host already showing a session badge?** Prod's data_view hides its
   * own `◷` clock and posts `session_badge_target` when
   * `__USER_DATA__.host_session_badge` is set, and art-studio / doc-digitizer /
   * plant-expert each concede in a `note` that native cannot express the gate
   * and therefore ships ONE of the two lanes — on an app that already draws the
   * badge the user sees both controls.
   *
   * It rides the feed rather than a new argument because it is host state that
   * changes with the surface (an item page sets it, an index page does not),
   * exactly like `settings`, and because that keeps `buildRenderContext`'s
   * signature — the one the studio, the app and every test share — unchanged.
   *
   * Absent ⇒ `{}` ⇒ `user.host_session_badge` reads null, which is what a
   * `when` on a web/studio surface should see. Never undefined: a missing root
   * makes `resolveArg` treat `user.x` as a LITERAL string.
   */
  const user = feed.user && typeof feed.user === 'object' && !Array.isArray(feed.user)
    ? feed.user
    : {};

  const root = {
    entries,
    settings,
    page_args: pageArgs,
    pending,
    user,
    appearance,
    locale,
    target_build: targetBuild || null,
    instance_id: feed.instance_id || null,
    widget_id: bp.widget_id || feed.widget_id || null,
    title: bp.title || feed.title || 'Widget',
    accent: bp.accent || null,
    // Under a `__` name, beside `__unsupported`, for the same reason: it is a
    // fact about the RENDER, not a value a blueprint may bind.
    __loading: Boolean(loading),
    // Admin-only diagnostic (the pinned-version badge PageShell draws on the entry
    // page). Mirrors the Flutter kit: the pinned label rides the blueprint JSON
    // (`__version_label`, folded in from `X-Widget-Version-Label` by whoever fetched
    // it) — never a value a blueprint author binds. Absent ⇒ null ⇒ no badge, so it
    // only ever surfaces where an admin/studio caller handed a labeled blueprint.
    blueprintVersionLabel: bp.__version_label
      ?? (feed.version_label || null)
      ?? (bp.__blueprint_source === 'repo' ? 'repo' : null),
    serverEnv: feed.server_env || null,
  };

  const specs = data.selectors || {};
  const memo = new Map();
  const inFlight = new Set();
  const unsupported = [];
  // The whole folded item set, folded ONCE and handed to every derive as the
  // read-only view beside its own `of` rows.
  const deriveFeed = { entries, folded: foldRows(entries, data.fold || {}) };

  const resolve = (name) => {
    if (name == null) return undefined;
    if (memo.has(name)) return memo.get(name);
    const spec = specs[name];
    if (!spec) return undefined;
    if (inFlight.has(name)) return undefined; // cyclic reference — fail soft
    inFlight.add(name);
    // Anything the DATA lane could not honour is collected here rather than
    // returned as a value: an unknown derive degrades to empty (and is listed),
    // an unknown selector op still fails closed as a bound marker.
    const onIssue = (issue) => { if (issue) unsupported.push({ selector: name, ...issue }); };
    const value = evalSelector(spec, {
      entries, foldSpec: data.fold || {}, resolve, root, feed: deriveFeed, onIssue,
    });
    inFlight.delete(name);
    if (value && value.__unsupported) unsupported.push({ selector: name, ...value });
    memo.set(name, value);
    root[name] = value;
    return value;
  };

  // The fold block itself is contract too: an unknown `strategy` degrades to
  // the default merge and says so, the same way an unknown derive does.
  for (const issue of validateFold(data.fold)) unsupported.push(issue);
  for (const [name, spec] of Object.entries(specs)) {
    if (!spec?.fold) continue;
    for (const issue of validateFold({ ...(data.fold || {}), ...spec.fold })) {
      unsupported.push({ selector: name, ...issue });
    }
  }

  // A PRELIMINARY `focusEntry`, resolved off the already-folded feed BEFORE any
  // selector runs — so a selector may scope ITSELF to the focused row:
  //
  //   "versionGroup": { "op":"filter", "from":"versionOrder",
  //                     "where": {"create_session_id":"focusEntry.create_session_id"} }
  //
  // Without it `focusEntry` existed only AFTER the selector loop, so that exact
  // path read `null` on the DATA lane while the very same path read correctly
  // in every prop and every `when` on the same page — a split no author can
  // see, and the reason art-studio's version strip could not draw at all.
  //
  // It reads the DEFAULT fold (`deriveFeed.folded`), which is the one row set
  // that exists before selectors do. That is deliberately the POOR copy: it
  // carries the payload but none of the annotations a derive adds. Every
  // selector-visible `focusEntry.*` is therefore a raw feed field — which is
  // what a self-scoping `where` wants anyway (an identity, a session key, a
  // date) — and the RICH copy replaces it below, before anything renders.
  root.focusEntry = findFocus(focusPool(deriveFeed.folded), pageArgs);

  for (const name of Object.keys(specs)) resolve(name);

  // `focusEntry` — the row a data_view / detail page is focused on, REFINED now
  // that every selector has run. The host passes `page_args.item_id` (or one of
  // the other focus tokens); we resolve it against every folded selector so the
  // same key works for meds, meals and wardrobe items, and the richest copy —
  // the annotated one — wins.
  root.focusEntry = findFocus(foldedRows(root, specs), pageArgs);

  // `focusGrid` — the SESSION a photo page is focused on. This is the other
  // half of the identity split: a capture spans many items, so a photo page
  // has no item_id at all. `grid_id` follows the one rule the host, the
  // Photos grid and the delete path all use: session_id || the photo PATH.
  root.focusGrid = buildFocusGrid(pageArgs);
  root.selectors = Object.fromEntries(Object.keys(specs).map((k) => [k, root[k]]));
  root.__unsupported = unsupported;

  /**
   * `__itemCount` — HOW MANY THINGS THIS WIDGET ACTUALLY HOLDS.
   *
   * `entries.length` answers a different question: did the feed carry any
   * BYTES. Every fold that DROPS rows makes the two disagree, and the first-run
   * gate (`widgetHoldsNothing`) reads the wrong one when they do: plant-expert's
   * live instance is two `care_log` rows against a plant whose `plant` event
   * never landed — an ORPHAN bucket, dropped by `fold.js` — so `entries.length`
   * is 2, `garden` is empty, the blueprint's `emptyGarden` correctly fires, and
   * the gate suppressed it. The page rendered NOTHING: no content and no empty
   * state. Same shape for a goal-only nutritionist feed and a fully tombstoned
   * medicine box.
   *
   * It is `allFolded` — every array-valued selector's rows, deduped by identity
   * — and NOT "are all the arrays empty", because `focusPool` is what makes it
   * true: a GROUP bucket has an `items` array and no identity of its own, so it
   * is not counted. plant-expert's `shelves` is a two-bucket group that is
   * length 2 with zero plants; counting buckets would leave the widget looking
   * permanently non-empty.
   *
   * Under a `__` name, beside `__loading`: a fact about the render, not a value
   * a blueprint may bind.
   */
  root.__itemCount = allFolded(root, specs).length;

  /** Resolve an id list (e.g. `outfit.garment_ids`) to folded rows. */
  root.resolveItems = (ids) => {
    const want = (Array.isArray(ids) ? ids : []).map(String);
    if (!want.length) return [];
    const pool = allFolded(root, specs);
    const byId = new Map();
    for (const r of pool) {
      for (const k of [r.item_id, r.id, r.entry_id]) {
        if (k != null && !byId.has(String(k))) byId.set(String(k), r);
      }
    }
    return want.map((id) => byId.get(id)).filter(Boolean);
  };

  return root;
}

/**
 * The identity keys a folded row may answer to, in the host's own order.
 *
 * The tail three are the OTHER anchors the fleet's locked focus flows use, and
 * they are here rather than in each blueprint because they are HOST tokens, not
 * widget ones: `latest_entry_id` is what a `field-merge` row is deleted and
 * patched by (the newest event, which is what a host long-press addresses),
 * `log_card_id` is the chat card a row was written from (a notes/plant "open
 * the note behind this card" lands with that and nothing else), and
 * `create_session_id` is the session anchor a photo-side entry point carries.
 * A row that answers to none of them is simply not focusable — as before.
 */
function rowKeys(r) {
  return [
    r.item_id, r.id, r.entry_id,
    r.latest_entry_id, r.log_card_id, r.create_session_id, r.source_session_id,
  ].filter((k) => k != null && k !== '').map(String);
}

/** The rows in one array that can be a focus subject (a bucket is not one). */
function focusPool(rows) {
  const out = [];
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r || typeof r !== 'object' || Array.isArray(r.items)) continue;
    if (!rowKeys(r).length) continue;
    out.push(r);
  }
  return out;
}

/**
 * EVERY row of every array-valued selector, in declaration order and NOT
 * deduped — the same id may appear in a raw fold AND in the derived selector
 * that annotates it, and which of those two a caller wants is the caller's
 * decision (see `findFocus`).
 */
function foldedRows(root, specs) {
  const out = [];
  for (const name of Object.keys(specs)) {
    if (!Array.isArray(root[name])) continue;
    out.push(...focusPool(root[name]));
  }
  return out;
}

/** Every row from every array-valued selector (deduped by identity). */
function allFolded(root, specs) {
  const seen = new Set();
  return foldedRows(root, specs).filter((r) => {
    const k = rowKeys(r)[0];
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/**
 * `{ grid_id, session_id, photo_url }` for a session-bound page, or null when
 * the page carries none of them (every item-bound page).
 */
function buildFocusGrid(pageArgs) {
  const sessionId = pageArgs?.session_id ?? null;
  const photoUrl = pageArgs?.photo_url ?? null;
  const gridId = pageArgs?.grid_id ?? sessionId ?? photoUrl ?? null;
  if (gridId == null) return null;
  return { grid_id: gridId, session_id: sessionId, photo_url: photoUrl };
}

/**
 * `focusEntry` — the row a data_view is focused on, resolved against EVERY
 * array-valued selector.
 *
 * THE RULE: the RICHEST match wins, ties go to the LAST declared selector.
 *
 * First-match-wins made selector DECLARATION ORDER load-bearing, which is a
 * trap no author can see coming: the same item usually appears twice — once in
 * the raw `{op:'fold'}` and once in the `{op:'derive'}` that annotates it — and
 * a blueprint that (naturally) declares the fold first bound the UNANNOTATED
 * copy, so every `focusEntry.<derived_field>` on the detail page came back
 * undefined while the grid, which binds the derived selector directly, looked
 * perfect. The only fix was to hand-order selectors in reverse, i.e. to encode
 * a runtime detail in the data lane's shape.
 *
 * "Richest" = most keys on the matched row. A derive only ever ADDS fields to
 * the row it annotates, so it is always at least as rich as its input, and
 * `>=` on the comparison makes the LAST selector win a genuine tie.
 *
 * THE TOKEN SET IS THE HOST'S, not one widget's. `item_id` is what the app
 * passes today, but the locked flows already open a detail page with three
 * other anchors — `focus_entry_id` (a page pushed from another page of the same
 * bundle), `log_card_id` (a chat card's "open this note") and
 * `source_session_id` (the photo-side entry point) — and a page opened with one
 * of those used to land on `entries[0]`, i.e. on somebody else's row, which is
 * the worst possible failure for a detail page. Widening is additive: an
 * unmatched token still returns null, never a wrong row.
 */
function findFocus(pool, pageArgs) {
  const want = pageArgs?.item_id
    ?? pageArgs?.entry_id
    ?? pageArgs?.id
    ?? pageArgs?.focus_entry_id
    ?? pageArgs?.log_card_id
    ?? pageArgs?.source_session_id
    ?? null;
  // No item_id at all ⇒ the FIRST row is the subject; it still gets the
  // richest-copy treatment, so an argument-less detail page reads the same
  // fields a targeted one does.
  const key = want != null ? String(want) : rowKeys(pool[0] || {})[0];
  if (key == null) return pool[0] || null;
  let best = null;
  for (const r of pool) {
    if (!rowKeys(r).includes(key)) continue;
    if (!best || Object.keys(r).length >= Object.keys(best).length) best = r;
  }
  return best;
}
